import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiBase } from '../api';
import { useAuth } from '../components/AuthContext';

function money(cents) {
  if (cents === null || cents === undefined) return '—';
  return `$${(Number(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
}

function formatDate(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

const statusStyles = {
  QUOTE: { bg: '#f3f4f6', text: '#374151', label: 'Quote' },
  PENDING: { bg: '#fef3c7', text: '#92400e', label: '⏳ Pending' },
  APPROVED: { bg: '#dbeafe', text: '#1e40af', label: '✓ Approved' },
  FUNDED: { bg: '#d1fae5', text: '#065f46', label: '💵 Funded' },
  SETTLED: { bg: '#e0e7ff', text: '#3730a3', label: '✅ Settled' },
  REJECTED: { bg: '#fee2e2', text: '#991b1b', label: '✗ Rejected' },
  CANCELLED: { bg: '#f3f4f6', text: '#6b7280', label: 'Cancelled' }
};

const tierColors = {
  GOLD: '🥇',
  SILVER: '🥈',
  BRONZE: '🥉',
  NEW: '🆕'
};

export default function CarrierFactoringHistory() {
  const nav = useNavigate();
  const apiBase = useMemo(() => getApiBase(), []);
  const { carrierCompanyId } = useAuth();

  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, [carrierCompanyId]);

  async function fetchData() {
    setLoading(true);
    setError('');
    try {
      const [historyRes, tierRes] = await Promise.all([
        fetch(`${apiBase}/factoring/history`, {
          headers: { 'x-carrier-company-id': carrierCompanyId }
        }),
        fetch(`${apiBase}/factoring/tier`, {
          headers: { 'x-carrier-company-id': carrierCompanyId }
        })
      ]);

      const historyData = await historyRes.json();
      const tierData = await tierRes.json();

      if (!historyRes.ok) throw new Error(historyData.error);
      if (!tierRes.ok) throw new Error(tierData.error);

      setRequests(historyData.requests || []);
      setStats(tierData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const totalAdvanced = requests
    .filter(r => ['FUNDED', 'SETTLED'].includes(r.status))
    .reduce((sum, r) => sum + r.netAdvanceCents, 0);

  const totalFees = requests
    .filter(r => ['FUNDED', 'SETTLED'].includes(r.status))
    .reduce((sum, r) => sum + r.feeCents, 0);

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0 }}>💵 Quick Pay History</h1>
        <button
          onClick={() => nav('/carrier/loads')}
          style={{ padding: '10px 16px', fontWeight: 700 }}
        >
          ← Back to Loads
        </button>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* Stats cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
          <div style={{ background: 'white', padding: 20, borderRadius: 12, border: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: 14, color: '#6b7280' }}>Your Tier</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>
              {tierColors[stats.currentTier]} {stats.currentTier}
            </div>
          </div>
          <div style={{ background: 'white', padding: 20, borderRadius: 12, border: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: 14, color: '#6b7280' }}>Completed Loads</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{stats.completedLoadsCount}</div>
          </div>
          <div style={{ background: 'white', padding: 20, borderRadius: 12, border: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: 14, color: '#6b7280' }}>On-Time Rate</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{stats.onTimeDeliveryRate.toFixed(0)}%</div>
          </div>
          <div style={{ background: 'white', padding: 20, borderRadius: 12, border: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: 14, color: '#6b7280' }}>Total Advanced</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#059669' }}>{money(totalAdvanced)}</div>
          </div>
        </div>
      )}

      {/* Requests table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>
      ) : requests.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, background: '#f9fafb', borderRadius: 12 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>💵</div>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>No Quick Pay requests yet</div>
          <div style={{ color: '#6b7280' }}>
            After picking up or delivering a load, you can request Quick Pay to get paid faster.
          </div>
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 700 }}>Load</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 700 }}>Route</th>
                <th style={{ padding: 12, textAlign: 'right', fontWeight: 700 }}>Load Value</th>
                <th style={{ padding: 12, textAlign: 'right', fontWeight: 700 }}>Advance</th>
                <th style={{ padding: 12, textAlign: 'right', fontWeight: 700 }}>Fee</th>
                <th style={{ padding: 12, textAlign: 'right', fontWeight: 700 }}>Net</th>
                <th style={{ padding: 12, textAlign: 'center', fontWeight: 700 }}>Status</th>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 700 }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {requests.map(req => {
                const statusStyle = statusStyles[req.status] || statusStyles.QUOTE;
                return (
                  <tr key={req.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                    <td style={{ padding: 12 }}>
                      <div style={{ fontWeight: 600 }}>{req.load?.referenceNumber || req.loadId.slice(0, 8)}</div>
                    </td>
                    <td style={{ padding: 12, fontSize: 14, color: '#6b7280' }}>
                      {req.load?.originCity}, {req.load?.originState} → {req.load?.destCity}, {req.load?.destState}
                    </td>
                    <td style={{ padding: 12, textAlign: 'right' }}>{money(req.loadAmountCents)}</td>
                    <td style={{ padding: 12, textAlign: 'right' }}>
                      {money(req.advanceAmountCents)}
                      <div style={{ fontSize: 12, color: '#9ca3af' }}>{req.advancePercentage}%</div>
                    </td>
                    <td style={{ padding: 12, textAlign: 'right', color: '#dc2626' }}>
                      -{money(req.feeCents)}
                      <div style={{ fontSize: 12, color: '#9ca3af' }}>{req.feePercentage}%</div>
                    </td>
                    <td style={{ padding: 12, textAlign: 'right', fontWeight: 700, color: '#059669' }}>
                      {money(req.netAdvanceCents)}
                    </td>
                    <td style={{ padding: 12, textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        background: statusStyle.bg,
                        color: statusStyle.text,
                        borderRadius: 20,
                        fontSize: 13,
                        fontWeight: 600
                      }}>
                        {statusStyle.label}
                      </span>
                    </td>
                    <td style={{ padding: 12, fontSize: 14, color: '#6b7280' }}>
                      {formatDate(req.requestedAt || req.createdAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
